<script setup>
import FormHelper from "@/components/Tools/FormHelper/FormHelper.vue";
import { defineProps } from "vue";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { useStore } from "@/core/services/store";
import { useFeatureArea } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatureArea";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import { useLoadFormatters } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useLoadFormatters";
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useMigration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigration";
import { useAppStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppStatus";
import {
  addDeactivatedFeature,
  resetDeactivatedFeatures
} from "@/composables/useSalesChannelManagementApp";
import { labelData } from "@/components/ExternalApps/SalesChannelManagementApp/utility/labelData";
import { useWizard } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useWizard";
import i18n from "@/core/plugins/vue-i18n";
import _ from "lodash";
import { useShopifyOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useShopifyOauth";
import { useAmazonOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAmazonOauth";
import { useMolliePosOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMolliePosOauth";
import { useEbayOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useEbayOauth";
import { useBexioOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useBexioOauth";
import { useEtsyOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useEtsyOauth";
import { useIntegrationStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegrationStatus";
import { useOauthAccountNotice } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauthAccountNotice";

defineProps({
  selectedArea: {
    type: String,
    required: true
  }
});

const { onFeaturesChanged } = useFeatures();
const { selectedIntegration, formValueChange } = useIntegration();
const { onFeaturesInput } = useFeatureArea();
const { loadFormatter } = useLoadFormatters();
const store = useStore();
const { hasOnlyMigration, hasMigration } = useAppStatus();
const { onChangeMigrationStatus, migrationStatus, migrationStatusForm } =
  useMigration();
const { handleReauthorize } = useIntegrationStatus();
const { accountNoticeField } = useOauthAccountNotice();

const isReauthorizeApp = computed(
  () => store.getters.appConfig?.showReauthorizeButton ?? false
);

const wizardCredentials = computed(() => {
  const credentialsForm = store.getters.form("wizard.credentials") ?? [];

  if (isReauthorizeApp.value) {
    return [
      ...credentialsForm,
      {
        type: "action",
        name: "reauthorize",
        action: () => handleReauthorize(selectedIntegration.value),
        label: i18n.t("salesChannelManagementApp.integrationStatus.reauthorize")
      }
    ];
  }

  const showTestConnection =
    store.getters.appConfig?.showTestConnection ?? false;

  if (!showTestConnection) return credentialsForm;

  const { validateCredentials } = useWizard(selectedIntegration);
  const validateCredentialsAction = {
    type: "action",
    action: validateCredentials,
    label: i18n.t("salesChannelManagementApp.validateCredentials")
  };

  const actions = [validateCredentialsAction];

  // Handle OAuth apps
  const oauthApps = {
    shopify: useShopifyOauth,
    amazon: useAmazonOauth,
    mollie: useMolliePosOauth,
    ebay: useEbayOauth,
    bexio: useBexioOauth,
    etsy: useEtsyOauth
  };

  const selectedAppName = store.getters?.selectedApp?.name;
  if (selectedAppName && oauthApps[selectedAppName]) {
    const { getAccessToken } = oauthApps[selectedAppName]();
    return [
      ...(accountNoticeField.value ? [accountNoticeField.value] : []),
      {
        type: "action",
        action: getAccessToken,
        label: i18n.t("salesChannelManagementApp.getTokenByOAuth", {
          name: store.getters?.selectedApp?.label
        })
      }
    ];
  }

  return [...credentialsForm, ...actions];
});

watch(
  () => migrationStatus.value.active,
  () => {
    toggleActiveFieldDisableStatus();
    selectedIntegration.value.migrationActive = migrationStatus.value.active;
  }
);

const unwatchFeatureChange = watch(
  () => selectedIntegration.value.features.value,
  () => {
    resetDeactivatedFeatures();

    for (const [key, value] of Object.entries(
      selectedIntegration.value.features.value
    )) {
      if (value !== false || !selectedIntegration.value?.[key]?.baseSettings)
        continue;

      addDeactivatedFeature(key);
    }
  }
);

function toggleActiveFieldDisableStatus() {
  let activeField = store.getters
    .form("wizard.baseData")
    .find(field => field.name === "active");

  if (!activeField || !hasMigration.value || hasOnlyMigration.value) return;
  activeField.disabled = migrationStatus.value.active;
}

onMounted(() => {
  if (!hasMigration.value || hasOnlyMigration.value) return;

  migrationStatus.value.active = _.cloneDeep(
    selectedIntegration.value?.migrationActive ?? false
  );

  toggleActiveFieldDisableStatus();
});

onUnmounted(() => {
  unwatchFeatureChange();
});
</script>

<template>
  <div>
    <template v-if="selectedArea === 'wizard'">
      <template v-if="selectedIntegration">
        <FormHelper
          v-if="!hasOnlyMigration && hasMigration"
          v-model="migrationStatus"
          :form="migrationStatusForm"
          :config="{
            title: $t(
              'salesChannelManagementApp.migration.changeMigrationTitle'
            ),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          @change="onChangeMigrationStatus($event)"
        />

        <FormHelper
          v-if="
            !store.getters?.areaBlacklist?.includes('wizard.baseData') ?? true
          "
          v-model="selectedIntegration.baseData.value"
          :form="store.getters.form('wizard.baseData')"
          :config="{
            title: $t('salesChannelManagementApp.livemode'),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          @change="formValueChange($event, 'baseData')"
        />
        <FormHelper
          v-if="store.getters.form('wizard.credentials') || isReauthorizeApp"
          v-model="selectedIntegration.credentials.value"
          :form="wizardCredentials"
          :config="{
            title: $t('salesChannelManagementApp.credentials'),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          @change="formValueChange($event, 'credentials')"
        />
        <FormHelper
          v-if="store.getters.form('wizard.tbOneChannelSelection')"
          v-model="selectedIntegration.tbOneChannelSelection.value"
          :config="{
            title: $t('salesChannelManagementApp.titles.tbOneChannelSelection'),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          :form="store.getters.form('wizard.tbOneChannelSelection')"
          @change="formValueChange($event, 'tbOneChannelSelection')"
        />
        <FormHelper
          v-if="store.getters.form('wizard.sftpData')"
          v-model="selectedIntegration.sftpData.value"
          :config="{
            title: $t('salesChannelManagementApp.titles.sftpData'),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          :form="store.getters.form('wizard.sftpData')"
          @change="formValueChange($event, 'sftpData')"
        />
        <FormHelper
          v-if="store.getters.form('wizard.features')"
          v-model="selectedIntegration.features.value"
          :form="store.getters.form('wizard.features')"
          :config="{
            title: $t('salesChannelManagementApp.features'),
            snippetPrefix: 'salesChannelManagementApp',
            labelColClass: 'col-5',
            fieldColClass: 'col-7',
            fieldColClassXl: 'col-7',
            loadingGroup: 'salesChannelManagementApp',
            ...(labelData ?? {})
          }"
          @change="
            event => {
              onFeaturesChanged(event);
              formValueChange(event, 'features');
            }
          "
          @input="onFeaturesInput"
        />
      </template>

      <slot></slot>
    </template>

    <template v-else>
      <FormHelper
        v-for="(form, key) in store.getters.form('settings')"
        :key="'advanced-settings-' + key"
        v-model="selectedIntegration.settings[key].value"
        class="form-helper-settings"
        :form="form || {}"
        :config="{
          snippetPrefix: 'salesChannelManagementApp',
          title: $t('salesChannelManagementApp.titles.settings.' + key),
          configValues: false,
          customVariables: ['scmaXentralFields'],
          labelStacked: selectedArea === 'productAssignment',
          ...(labelData ?? {}),
          labelColClass: 'col-5',
          fieldColClass: 'col-7',
          fieldColClassXl: 'col-7',
          loadingGroup: 'salesChannelManagementApp'
        }"
        @change="formValueChange($event, 'settings', key)"
        @load-formatter="loadFormatter"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.form-helper {
  border-bottom: 1px solid #d0d5dd;
  padding: 1rem 0 2rem 0;
  margin: 1rem 0;

  &-settings {
    &:last-of-type {
      border-bottom: none;
    }
  }
}
</style>
